import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import {
  Download,
  Upload,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  UserCheck,
  PhoneCall,
  Workflow,
  MessageCircle,
  UserPlus,
  Trash2
} from 'lucide-react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Search from '../../shared/components/ui/Search';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import PaginationComponent from '../../shared/components/PaginationComponent';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import UploadResultsModal from '../components/ui/UploadResultsModal';
import PreleadUpdateStatusModal from '../components/ui/PreleadUpdateStatusModal';
import PreleadLogModal, { PreleadNoteButton } from '../components/ui/PreleadLogModal';
import PreleadListAutoWhatsAppFlow from '../components/prelead/PreleadListAutoWhatsAppFlow';
import WhatsAppFlowSendStatusChip from '../components/prelead/WhatsAppFlowSendStatusChip';
import PreleadSlaIndicator from '../components/prelead/PreleadSlaIndicator';
import BulkAssignPreleadsModal from '../components/prelead/BulkAssignPreleadsModal';
import BulkSendFlowModal from '../components/whatsapp/BulkSendFlowModal';
import { useBuilderWhatsappIntegration } from '../hooks/useBuilderWhatsappIntegration';
import { normalizeLeadPhone } from '../utils/leadWhatsAppUtils';
import { normalizeTemplateUploadResponse } from '../utils/templateUploadResponse';
import {
  addPreLeadNote,
  bulkAssignPreLeads,
  bulkDeletePreLeads,
  createLead,
  deletePreLead,
  downloadPreLeadTemplate,
  getPreLeadMetaFields,
  getPreLeadGoogleAdsFields,
  getPreLeadWebsiteFormFields,
  getPreLeadsByListId,
  markPreLeadAsUnqualified,
  uploadPreLeadTemplate
} from '../services/api/implementation/impl';
import { resolvePreleadQualifyProjectId } from '../utils/preleadQualifyLeadForm';
import { capitalizeWords, formatStatusLabel } from '../utils/stringUtils';
import {
  getPreleadListFilterQuery,
  normalizePreleadListFilter,
  PRELEAD_LIST_FILTER_CONFIG,
  PRELEAD_LIST_FILTER_TABS
} from '../utils/preleadListFilters';
import {
  formatPreleadStatusNote,
  PRELEAD_STATUS_CATEGORY
} from '../utils/preleadStatusOptions';

/** Max preleads per WhatsApp flow send (API limit). */
/** UI selection cap; sends are chunked in batches of 50 server-side by BulkSendFlowModal. */
const WA_MAX_PRELEADS = 500;
/** Max preleads per bulk assign (API limit). */
const ASSIGN_MAX_PRELEADS = 100;
/** Max preleads per bulk delete (API limit). */
const DELETE_MAX_PRELEADS = 100;

function isPreleadDeletable(row) {
  const state = String(row?.state ?? '').toLowerCase();
  return state !== 'interested' && state !== 'not_interested';
}

function formatCellDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizePreleadState(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function getRelevantCampaignSend(sends, preleadState) {
  if (!Array.isArray(sends) || sends.length === 0) return null;
  const normalizedState = normalizePreleadState(preleadState);
  const byLatestSentAt = (a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0);
  const latestSend = [...sends].sort(byLatestSentAt)[0];
  const interestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'interested')
    .sort(byLatestSentAt)[0];
  const notInterestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'not_interested')
    .sort(byLatestSentAt)[0];

  if (normalizedState === 'interested') return interestedSend ?? latestSend;
  if (normalizedState === 'not_interested') return notInterestedSend ?? latestSend;
  return latestSend;
}

function CampaignChips({ sends, preleadState }) {
  if (!sends?.length) return <span className="text-gray-400 text-xs">—</span>;
  const campaignToShow = getRelevantCampaignSend(sends, preleadState);
  const name = campaignToShow?.WhatsAppCampaign?.name ?? '—';
  return (
    <span
      title={name}
      className="inline-flex items-center gap-1 rounded-md bg-customBlue/10 px-2 py-0.5 text-[11px] font-medium text-customBlue max-w-[9rem] truncate">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-customBlue/60" />
      <span className="truncate">{name}</span>
    </span>
  );
}

function getAnswerLabel(answer) {
  if (!answer) return '—';
  return (
    answer?.questionOption?.label ||
    answer?.question_option?.label ||
    answer?.raw_text ||
    answer?.raw_payload ||
    '—'
  );
}

function getQuestionOptionLabels(answer) {
  const optionBuckets = [
    answer?.question?.options,
    answer?.question?.questionOptions,
    answer?.question?.question_options,
    answer?.question_options,
    answer?.options
  ];

  const flattenedOptions = optionBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
  const labels = flattenedOptions
    .map((option) =>
      String(
        option?.label ?? option?.option_label ?? option?.text ?? option?.title ?? option?.value ?? ''
      ).trim()
    )
    .filter(Boolean);

  return Array.from(new Set(labels));
}

function formatMetaFieldQuestion(question) {
  if (!question) return '—';
  return String(question)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function PreleadFormSummaryCard({
  prelead,
  formMeta,
  formMetaLabel = 'Form',
  showWhatsAppAutoSend = false
}) {
  const email = prelead?.email?.trim() || '—';
  const status = prelead?.state ? formatStatusLabel(String(prelead.state)) : '—';
  const formName = formMeta?.name || null;
  const projectName = formMeta?.project_name?.trim() || null;

  if (!prelead && !formMeta) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {formMetaLabel}
        </span>
        {formName && <span className="text-sm font-medium text-gray-900">{formName}</span>}
        {projectName && <span className="text-xs text-gray-500">· {projectName}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-gray-700">
          <span className="text-gray-400">Email </span>
          <span className="font-medium text-gray-900 break-all">{email}</span>
        </span>
        <span className="text-gray-700">
          <span className="text-gray-400">Status </span>
          <span className="font-medium text-gray-900">{status}</span>
        </span>
        {showWhatsAppAutoSend && (
          <span className="inline-flex items-center gap-1.5 text-gray-700">
            <span className="text-gray-400">WA Auto-send</span>
            <WhatsAppFlowSendStatusChip row={prelead} />
          </span>
        )}
      </div>
    </div>
  );
}

function FacebookMetaFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadMetaFields', preLeadId],
    queryFn: () => getPreLeadMetaFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const form = data?.form;

  return (
    <Modal width={50} name="Facebook Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || form || isLoading) && (
          <PreleadFormSummaryCard
            prelead={prelead}
            formMeta={form}
            formMetaLabel="Form"
            showWhatsAppAutoSend
          />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function GoogleAdsFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadGoogleAdsFields', preLeadId],
    queryFn: () => getPreLeadGoogleAdsFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Google Ads Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard
            prelead={prelead}
            formMeta={source}
            formMetaLabel="Source"
          />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function WebsiteFormFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadWebsiteFormFields', preLeadId],
    queryFn: () => getPreLeadWebsiteFormFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });
  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Website / Framer Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Form" />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          fields.length > 0 ? (
            fields.map((field, index) => (
              <div
                key={field.id ?? `${field.question}-${index}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {formatMetaFieldQuestion(field.question)}
                </p>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                  <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                    {field.answer || '—'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No form fields available.</p>
          )
        )}
      </div>
    </Modal>
  );
}

function WhatsAppFlowAnswersModal({ selectedPrelead, onClose }) {
  const answers = selectedPrelead?.whatsapp_flow_question_answers ?? [];

  return (
    <Modal width={50} name="WhatsApp Flow Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {answers.length > 0 ? (
          answers
            .slice()
            .sort((a, b) => (a?.question?.question_order ?? 999) - (b?.question?.question_order ?? 999))
            .map((answer, index) => {
              const selectedAnswer = getAnswerLabel(answer);
              const selectedAnswerNorm = selectedAnswer.toLowerCase().trim();
              const otherOptions = getQuestionOptionLabels(answer).filter(
                (label) => label.toLowerCase().trim() !== selectedAnswerNorm
              );

              return (
                <div
                  key={answer.id ?? `${answer.question_id}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Question {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{answer?.question?.prompt_text || '—'}</p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Selected Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900">{selectedAnswer}</p>
                  </div>

                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-customBlue">Other Options</p>
                    {otherOptions.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {otherOptions.map((optionLabel) => (
                          <span
                            key={optionLabel}
                            className="inline-flex rounded-full border border-customBlue/20 bg-customBlue/5 px-2.5 py-1 text-xs text-customBlue">
                            {optionLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">No other options captured.</p>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <p className="text-sm text-gray-500">No WhatsApp flow answers available.</p>
        )}
      </div>
    </Modal>
  );
}

function getPreleadNormalizedPhone(row) {
  const combined = [row?.countryCode, row?.phone].filter(Boolean).join(' ').trim();
  return normalizeLeadPhone(combined || row?.phone_display);
}

function PreleadTable({
  rows,
  showAction = true,
  showFlowResponse = true,
  showFacebookFormResponse = false,
  showGoogleAdsFormResponse = false,
  showWebsiteFormResponse = false,
  showWhatsAppCampaign = true,
  showWhatsAppInbox = false,
  listProjectId = null,
  selectionMode = false,
  selectedIds = [],
  selectableIds = [],
  selectAllChecked = false,
  onToggleSelect,
  onSelectAll,
  selectionTitle = 'Select prelead',
  onUpdateStatus,
  onLogAdded,
  onDeleteRow,
  isStatusSubmitting = false,
  emptyMessage = 'No preleads found.'
}) {
  const navigate = useNavigate();
  const [statusUpdateRow, setStatusUpdateRow] = useState(null);
  const [selectedPreleadForAnswers, setSelectedPreleadForAnswers] = useState(null);
  const [selectedPreleadForMetaFields, setSelectedPreleadForMetaFields] = useState(null);
  const [selectedPreleadForGoogleAdsFields, setSelectedPreleadForGoogleAdsFields] = useState(null);
  const [selectedPreleadForWebsiteFields, setSelectedPreleadForWebsiteFields] = useState(null);
  const [selectedPreleadForLogs, setSelectedPreleadForLogs] = useState(null);
  const interestedState = 'interested';

  const showFormResponseDetails =
    showFacebookFormResponse || showGoogleAdsFormResponse || showWebsiteFormResponse;

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'phone_display', label: 'Phone' },
    // Email / Status live in the Form Response modal when that column is shown.
    ...(!showFormResponseDetails
      ? [
          { key: 'email', label: 'Email' },
          { key: 'state', label: 'Status' }
        ]
      : []),
    ...(showWhatsAppCampaign
      ? [{ key: 'whatsapp_campaign_sends', label: 'WhatsApp Campaigns', custom: true }]
      : []),
    { key: 'project_name', label: 'Project Name' },
    { key: 'assigned', label: 'Assigned' },
    ...(showFlowResponse ? [{ key: 'whatsapp_flow_question_answers', label: 'Flow Response', custom: true }] : []),
    ...(showFacebookFormResponse
      ? [{ key: 'facebook_meta_fields', label: 'Form Response', custom: true }]
      : []),
    ...(showGoogleAdsFormResponse
      ? [{ key: 'google_ads_meta_fields', label: 'Form Response', custom: true }]
      : []),
    ...(showWebsiteFormResponse
      ? [{ key: 'website_form_fields', label: 'Form Response', custom: true }]
      : []),
    { key: 'created_at', label: 'Created' },
    { key: 'logs', label: 'Notes', custom: true }
  ];

  const colSpan =
    columns.length + (showAction ? 1 : 0) + (selectionMode ? 1 : 0) + (showWhatsAppInbox ? 1 : 0);

  const openWhatsAppConversation = (row) => {
    const normalized = getPreleadNormalizedPhone(row);
    if (!normalized) {
      toast.error('No valid phone number for this prelead.');
      return;
    }
    const rowProjectId = Number(row.project_id);
    const projectId =
      (Number.isFinite(rowProjectId) && rowProjectId > 0 ? rowProjectId : null) ||
      listProjectId ||
      '';
    navigate(`/builder/sales/whatsapp/${normalized}`, {
      state: {
        contactName: row.name || '',
        leadId: row.lead_id || null,
        projectId
      }
    });
  };

  const renderActionCell = (row) => {
    const currentState = String(row.state ?? '').toLowerCase();
    const isInterested = currentState === interestedState;
    const hasLead = row.lead_id != null;
    const isUnqualified = row.is_disqualified === true;
    const showDelete = typeof onDeleteRow === 'function' && isPreleadDeletable(row);

    const deleteBtn = showDelete ? (
      <button
        type="button"
        title="Delete prelead"
        aria-label={`Delete ${row.name || 'prelead'}`}
        onClick={() => onDeleteRow(row)}
        className="inline-flex items-center justify-center rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-rose-600 hover:bg-rose-100 transition-colors">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    ) : null;

    if (!isInterested) {
      return deleteBtn || '—';
    }
    if (hasLead) {
      return (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <div className="text-green-700 font-semibold">Qualified</div>
        </div>
      );
    }
    if (isUnqualified) {
      return (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <div className="text-red-700 font-semibold">Unqualified</div>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => setStatusUpdateRow(row)}
          className="inline-flex items-center rounded-lg border border-customBlue/50 bg-[#E9F5F6] px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/15 transition-colors">
          Update Status
        </button>
        {deleteBtn}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-[24rem] overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-[#E9F5F6] text-customBlue">
            {selectionMode && (
              <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15 w-10">
                <input
                  type="checkbox"
                  checked={selectAllChecked}
                  onChange={(e) => onSelectAll?.(e.target.checked)}
                  disabled={selectableIds.length === 0}
                  className="h-4 w-4 rounded border-gray-300 text-customBlue focus:ring-customBlue"
                  aria-label="Select all preleads on this page"
                  title="Select all on this page"
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.key}
                className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                {c.label}
              </th>
            ))}
            {showWhatsAppInbox && (
              <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                WhatsApp
              </th>
            )}
            {showAction && (
              <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const canSelect = selectableIds.includes(row.id);
            const isSelected = selectedIds.includes(row.id);
            const normalizedPhone = getPreleadNormalizedPhone(row);
            return (
            <tr key={row.id} className="hover:bg-gray-50/80">
              {selectionMode && (
                <td className="px-3 py-2">
                  {canSelect ? (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onToggleSelect?.(row.id, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-customBlue focus:ring-customBlue"
                      title={selectionTitle}
                      aria-label={`Select ${row.name || 'prelead'}`}
                    />
                  ) : (
                    <span className="inline-block w-4" title="Not selectable" />
                  )}
                </td>
              )}
              {columns.map((c) =>
                c.custom ? (
                  <td key={c.key} className="px-3 py-2">
                    {c.key === 'whatsapp_campaign_sends' ? (
                      <CampaignChips sends={row[c.key]} preleadState={row.state} />
                    ) : c.key === 'facebook_meta_fields' ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForMetaFields(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Form
                      </button>
                    ) : c.key === 'google_ads_meta_fields' ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForGoogleAdsFields(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Form
                      </button>
                    ) : c.key === 'website_form_fields' ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForWebsiteFields(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Form
                      </button>
                    ) : c.key === 'logs' ? (
                      <PreleadNoteButton
                        row={row}
                        onClick={() => setSelectedPreleadForLogs(row)}
                      />
                    ) : Array.isArray(row.whatsapp_flow_question_answers) &&
                      row.whatsapp_flow_question_answers.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForAnswers(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Q&A
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                ) : (
                  <td
                    key={c.key}
                    className="px-3 py-2 text-gray-800 max-w-[12rem] truncate"
                    title={
                      c.key === 'name'
                        ? undefined
                        : String(row[c.key] ?? '')
                    }>
                    {row[c.key] === null || row[c.key] === undefined || row[c.key] === ''
                      ? '—'
                      : c.key === 'name' ? (
                          <span className="inline-flex items-center gap-2 min-w-0">
                            <PreleadSlaIndicator slaColor={row.sla_color} />
                            <span className="truncate" title={capitalizeWords(String(row[c.key]))}>
                              {capitalizeWords(String(row[c.key]))}
                            </span>
                          </span>
                        ) : c.key === 'state'
                          ? formatStatusLabel(String(row[c.key]))
                          : String(row[c.key])}
                  </td>
                )
              )}
              {showWhatsAppInbox && (
                <td className="px-3 py-2 text-center">
                  {normalizedPhone ? (
                    <button
                      type="button"
                      title="Open WhatsApp conversation"
                      aria-label="Open WhatsApp conversation"
                      onClick={() => openWhatsAppConversation(row)}
                      className="relative inline-flex items-center justify-center rounded-full p-1.5 text-green-600 transition-colors hover:bg-green-50">
                      <MessageCircle className="h-5 w-5" strokeWidth={2} />
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
              )}
              {showAction && (
                <td className="px-3 py-2 text-center">
                  {renderActionCell(row)}
                </td>
              )}
            </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={colSpan}
                className="px-3 py-8 text-center text-sm text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

      <PreleadUpdateStatusModal
        open={Boolean(statusUpdateRow)}
        prelead={statusUpdateRow}
        isSubmitting={isStatusSubmitting}
        onClose={() => {
          if (isStatusSubmitting) return;
          setStatusUpdateRow(null);
        }}
        onConfirm={async (payload) => {
          if (!statusUpdateRow || isStatusSubmitting) return;
          await onUpdateStatus?.(statusUpdateRow, payload);
          setStatusUpdateRow(null);
        }}
      />
      {selectedPreleadForAnswers && (
        <WhatsAppFlowAnswersModal
          selectedPrelead={selectedPreleadForAnswers}
          onClose={() => setSelectedPreleadForAnswers(null)}
        />
      )}
      {selectedPreleadForMetaFields && (
        <FacebookMetaFieldsModal
          preLeadId={selectedPreleadForMetaFields.id}
          prelead={selectedPreleadForMetaFields}
          onClose={() => setSelectedPreleadForMetaFields(null)}
        />
      )}
      {selectedPreleadForGoogleAdsFields && (
        <GoogleAdsFieldsModal
          preLeadId={selectedPreleadForGoogleAdsFields.id}
          prelead={selectedPreleadForGoogleAdsFields}
          onClose={() => setSelectedPreleadForGoogleAdsFields(null)}
        />
      )}
      {selectedPreleadForWebsiteFields && (
        <WebsiteFormFieldsModal
          preLeadId={selectedPreleadForWebsiteFields.id}
          prelead={selectedPreleadForWebsiteFields}
          onClose={() => setSelectedPreleadForWebsiteFields(null)}
        />
      )}
      {selectedPreleadForLogs && (
        <PreleadLogModal
          prelead={selectedPreleadForLogs}
          onClose={() => setSelectedPreleadForLogs(null)}
          onLogAdded={onLogAdded}
        />
      )}
    </>
  );
}

const PreleadListDetailPage = () => {
  const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
  const { listId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { collapsed } = useSidebar();
  const queryClient = useQueryClient();
  const listMeta = location.state || {};

  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(25);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get('search') || '').trim()
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [isUploadResultModalOpen, setIsUploadResultModalOpen] = useState(false);
  const [isAutoFlowModalOpen, setIsAutoFlowModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccesses, setUploadSuccesses] = useState([]);
  const [uploadDuplicates, setUploadDuplicates] = useState([]);
  const [waBulkMode, setWaBulkMode] = useState(false);
  const [assignBulkMode, setAssignBulkMode] = useState(false);
  const [deleteBulkMode, setDeleteBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [waFlowPickerOpen, setWaFlowPickerOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState(null);

  const { allowWhatsAppCampaignsSidebar } = useBuilderWhatsappIntegration();

  const numericListId = Number(listId);
  const selectedFilter = normalizePreleadListFilter(searchParams.get('filter'));
  const filterQuery = useMemo(() => getPreleadListFilterQuery(selectedFilter), [selectedFilter]);
  const filterState = filterQuery.state ?? null;
  const filterIsDisqualified =
    typeof filterQuery.is_disqualified === 'boolean' ? filterQuery.is_disqualified : null;
  const filterConvertedToLead =
    typeof filterQuery.converted_to_lead === 'boolean' ? filterQuery.converted_to_lead : null;

  const setFilterInQuery = (nextFilter) => {
    const normalized = normalizePreleadListFilter(nextFilter);
    const nextParams = new URLSearchParams(searchParams);
    if (!normalized || normalized === 'all') {
      nextParams.delete('filter');
    } else {
      nextParams.set('filter', normalized);
    }
    nextParams.delete('state');
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchInput.trim();
      setDebouncedSearch(trimmed);
      setSearchParams(
        (prev) => {
          const current = prev.get('search') || '';
          if (trimmed === current) return prev;
          const nextParams = new URLSearchParams(prev);
          if (trimmed) nextParams.set('search', trimmed);
          else nextParams.delete('search');
          return nextParams;
        },
        { replace: true }
      );
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [selectedFilter, debouncedSearch, pageLimit]);

  useEffect(() => {
    setBulkSelectedIds([]);
    setWaFlowPickerOpen(false);
    setAssignModalOpen(false);
  }, [selectedFilter, debouncedSearch]);

  const {
    data: preleadsData = { overall: null, preLeads: [], meta: { page: 1, limit: pageLimit, totalPages: 1 } },
    isLoading,
    isError,
    error,
    isFetching
  } = useQuery({
    queryKey: [
      'preLeadsByListId',
      numericListId,
      page,
      pageLimit,
      filterState,
      filterIsDisqualified,
      filterConvertedToLead,
      debouncedSearch
    ],
    queryFn: () =>
      getPreLeadsByListId({
        preLeadListId: numericListId,
        page,
        limit: pageLimit,
        search: debouncedSearch || undefined,
        ...filterQuery
      }),
    enabled: Number.isFinite(numericListId) && numericListId > 0,
    keepPreviousData: true
  });

  const preleads = preleadsData.preLeads;
  const apiList = preleadsData.list;
  const overallStats = preleadsData.overall;
  const title = apiList?.name?.trim() || listMeta.listName || `Prelead list #${listId}`;
  const listSource = apiList?.source ?? listMeta.source ?? '';
  const isFacebookList = String(listSource).trim().toLowerCase() === 'facebook';
  const isGoogleAdsList = String(listSource).trim().toLowerCase() === 'google_ads';
  const isWebsiteList = String(listSource).trim().toLowerCase() === 'website';
  const isInboundAdsList = isFacebookList || isGoogleAdsList || isWebsiteList;
  const companyLabel =
    apiList?.company_name?.trim() ||
    (apiList?.company_id != null ? `Company ${apiList.company_id}` : null) ||
    (listMeta.companyId != null && listMeta.companyId !== ''
      ? `Company ${listMeta.companyId}`
      : null);
  const interestedCount = overallStats?.interested_count ?? overallStats?.interested ?? 0;
  const unqualifiedCount = overallStats?.not_interested_count ?? overallStats?.not_interested ?? 0;
  const totalPreLeadsCount = overallStats?.total_pre_leads ?? 0;
  const pendingCount = overallStats?.pending_count ?? overallStats?.pending ?? 0;
  const linkedToLeadCount = overallStats?.linked_to_lead ?? 0;
  const pendingTelecallingCount = overallStats?.pending_telecalling ?? 0;
  const paginationMeta = preleadsData.meta;

  const resolvedProjectId = useMemo(() => {
    const candidates = [
      apiList?.project_id,
      preleadsData.project_id,
      apiList?.whatsapp_flow?.project_id
    ];
    for (const candidate of candidates) {
      const n = Number(candidate);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }, [apiList, preleadsData.project_id]);

  const tableRows = useMemo(
    () =>
      preleads.map((p) => ({
        id: p.id,
        pre_lead_id: p.id,
        name: p.name ?? '',
        countryCode: p.countryCode ?? '',
        phone: p.phone ?? '',
        phone_display: [p.countryCode, p.phone].filter(Boolean).join(' ').trim() || '—',
        email: p.email ?? null,
        state: p.state ?? '—',
        whatsapp_campaign_sends: p.whatsapp_campaign_sends ?? [],
        project_id: p.project_id ?? null,
        project_name: isInboundAdsList
          ? p.project_name?.trim() || preleadsData.project_name?.trim() || '—'
          : getRelevantCampaignSend(p.whatsapp_campaign_sends, p.state)?.WhatsAppCampaign?.project?.name?.trim() ||
            '—',
        assigned: p.sales?.name?.trim() || 'No Assigned',
        sales_id: p.sales_id ?? p.sales?.id ?? null,
        alternate_phone_number: p.alternate_phone_number ?? null,
        min_estimated_budget: p.min_estimated_budget ?? null,
        max_estimated_budget: p.max_estimated_budget ?? null,
        occupation: p.occupation ?? null,
        reference_name: p.reference_name ?? null,
        type: p.type ?? null,
        expected_budget: p.expected_budget ?? null,
        expected_closure_date: p.expected_closure_date ?? null,
        accommodations: p.accommodations ?? [],
        project_accommodations: p.project_accommodations ?? [],
        plotArea: p.plotArea ?? p.plot_area ?? [],
        address: p.address ?? null,
        gender: p.gender ?? null,
        source: p.source ?? null,
        is_disqualified: p.is_disqualified ?? false,
        lead_id: p.lead?.id ?? null,
        whatsapp_flow_question_answers: p.whatsapp_flow_question_answers ?? [],
        whatsapp_flow_send_status: p.whatsapp_flow_send_status ?? null,
        whatsapp_flow_scheduled_at: p.whatsapp_flow_scheduled_at ?? null,
        whatsapp_flow_sent_at: p.whatsapp_flow_sent_at ?? null,
        whatsapp_flow_delivered_at: p.whatsapp_flow_delivered_at ?? null,
        whatsapp_flow_send_error: p.whatsapp_flow_send_error ?? null,
        minutes_until_send: p.minutes_until_send ?? null,
        minutes_after_lead: p.minutes_after_lead ?? null,
        sla_status: p.sla_status ?? null,
        sla_color: p.sla_color ?? 'none',
        sla_elapsed_business_minutes: p.sla_elapsed_business_minutes ?? null,
        sla_remaining_business_minutes: p.sla_remaining_business_minutes ?? null,
        sla_first_action_at: p.sla_first_action_at ?? null,
        sla_first_action_type: p.sla_first_action_type ?? null,
        first_action_at: p.first_action_at ?? null,
        first_action_type: p.first_action_type ?? null,
        reason: p.reason ?? null,
        log: p.log ?? null,
        legacy_log: p.legacy_log ?? p.legacyLog ?? null,
        telecalling_log: p.telecalling_log ?? null,
        notes_count: p.notes_count ?? p.notesCount ?? 0,
        notes: Array.isArray(p.notes) ? p.notes : [],
        created_at: formatCellDate(p.created_at)
      })),
    [preleads, isInboundAdsList, preleadsData.project_name]
  );

  const waSelectableIds = useMemo(
    () =>
      tableRows
        .filter((row) => {
          const phone = String(row.phone ?? '').trim();
          return phone.length > 0 && phone !== '—' && phone !== '---';
        })
        .map((row) => row.id),
    [tableRows]
  );

  const assignSelectableIds = useMemo(() => tableRows.map((row) => row.id), [tableRows]);
  const deleteSelectableIds = useMemo(
    () => tableRows.filter((row) => isPreleadDeletable(row)).map((row) => row.id),
    [tableRows]
  );

  const selectionMode = waBulkMode || assignBulkMode || deleteBulkMode;
  const maxSelected = deleteBulkMode
    ? DELETE_MAX_PRELEADS
    : assignBulkMode
      ? ASSIGN_MAX_PRELEADS
      : WA_MAX_PRELEADS;
  const bulkSelectableIds = deleteBulkMode
    ? deleteSelectableIds
    : assignBulkMode
      ? assignSelectableIds
      : waSelectableIds;

  const bulkSelectAllChecked =
    bulkSelectableIds.length > 0 &&
    bulkSelectableIds.slice(0, maxSelected).every((id) => bulkSelectedIds.includes(id));

  const toggleBulkPrelead = (preLeadId, checked) => {
    setBulkSelectedIds((prev) => {
      if (!checked) return prev.filter((id) => id !== preLeadId);
      if (prev.includes(preLeadId)) return prev;
      if (prev.length >= maxSelected) {
        toast.error(`You can select up to ${maxSelected} preleads at a time.`);
        return prev;
      }
      return [...prev, preLeadId];
    });
  };

  const handleBulkSelectAll = (checked) => {
    if (!checked) {
      setBulkSelectedIds((prev) => prev.filter((id) => !bulkSelectableIds.includes(id)));
      return;
    }
    const pageIds = bulkSelectableIds;
    setBulkSelectedIds((prev) => {
      const next = [...prev];
      for (const id of pageIds) {
        if (next.includes(id)) continue;
        if (next.length >= maxSelected) break;
        next.push(id);
      }
      return next;
    });
    const alreadySelectedOnPage = pageIds.filter((id) => bulkSelectedIds.includes(id)).length;
    const remainingSlots = Math.max(0, maxSelected - bulkSelectedIds.length);
    const newFromPage = pageIds.length - alreadySelectedOnPage;
    if (newFromPage > remainingSlots) {
      toast.error(`You can select up to ${maxSelected} preleads at a time.`);
    }
  };

  const exitBulkModes = () => {
    setWaBulkMode(false);
    setAssignBulkMode(false);
    setDeleteBulkMode(false);
    setBulkSelectedIds([]);
    setWaFlowPickerOpen(false);
    setAssignModalOpen(false);
  };

  const enterWaBulkMode = () => {
    setAssignBulkMode(false);
    setDeleteBulkMode(false);
    setAssignModalOpen(false);
    setBulkSelectedIds([]);
    setWaBulkMode(true);
  };

  const enterAssignBulkMode = () => {
    setWaBulkMode(false);
    setDeleteBulkMode(false);
    setWaFlowPickerOpen(false);
    setBulkSelectedIds([]);
    setAssignBulkMode(true);
  };

  const enterDeleteBulkMode = () => {
    setWaBulkMode(false);
    setAssignBulkMode(false);
    setWaFlowPickerOpen(false);
    setAssignModalOpen(false);
    setBulkSelectedIds([]);
    setDeleteBulkMode(true);
  };

  const handleOpenWaFlowPicker = () => {
    if (bulkSelectedIds.length === 0) {
      toast.error('Select at least one prelead to send a flow.');
      return;
    }
    setWaFlowPickerOpen(true);
  };

  const handleOpenAssignModal = () => {
    if (bulkSelectedIds.length === 0) {
      toast.error('Select at least one prelead to assign.');
      return;
    }
    setAssignModalOpen(true);
  };

  const bulkAssignMutation = useMutation({
    mutationFn: ({ pre_lead_ids, sales_id }) => bulkAssignPreLeads({ pre_lead_ids, sales_id }),
    onSuccess: (_data, variables) => {
      const count = Array.isArray(variables?.pre_lead_ids) ? variables.pre_lead_ids.length : 0;
      toast.success(`Assigned ${count} pre-lead${count === 1 ? '' : 's'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      exitBulkModes();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to assign pre-leads'
      );
    }
  });

  const handleBulkAssignConfirm = async (salesId) => {
    await bulkAssignMutation.mutateAsync({
      pre_lead_ids: bulkSelectedIds,
      sales_id: salesId
    });
  };

  const deletePreleadsMutation = useMutation({
    mutationFn: (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) {
        return Promise.reject(new Error('No preleads selected'));
      }
      if (ids.length === 1) return deletePreLead(ids[0]);
      return bulkDeletePreLeads({ ids });
    },
    onSuccess: (data, ids) => {
      const deletedCount = Array.isArray(data?.deleted)
        ? data.deleted.length
        : Array.isArray(ids)
          ? ids.length
          : 1;
      const skippedCount = Array.isArray(data?.skipped) ? data.skipped.length : 0;
      toast.success(
        deletedCount > 1
          ? `${deletedCount} preleads deleted`
          : 'Prelead deleted successfully'
      );
      if (skippedCount > 0) {
        toast.error(`${skippedCount} prelead(s) could not be deleted`);
      }
      setPendingDeleteIds(null);
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
      exitBulkModes();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete prelead(s)');
    }
  });

  const handleConfirmBulkDelete = () => {
    if (bulkSelectedIds.length === 0) {
      toast.error('Select at least one prelead to delete.');
      return;
    }
    setPendingDeleteIds([...bulkSelectedIds]);
  };

  const createPreleadLeadMutation = useMutation({
    mutationFn: (payload) => createLead(payload)
  });
  const unqualifyPreleadMutation = useMutation({
    mutationFn: ({ preLeadId, reason }) => markPreLeadAsUnqualified(preLeadId, reason)
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const qualifyLeadSource = isFacebookList
    ? 'facebook'
    : isGoogleAdsList
      ? 'google_ads'
      : isWebsiteList
        ? 'website'
        : null;

  const invalidateAfterLeadCreate = () => {
    queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
    queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
    queryClient.invalidateQueries({ queryKey: ['pendingTelecalling'] });
    // Sales main Leads list uses queryKey ['lead', ...]
    queryClient.invalidateQueries({ queryKey: ['lead'] });
    queryClient.invalidateQueries({ queryKey: ['leadStatsCards'] });
  };

  /**
   * Immediately create CRM lead (same as Pending Telecalling).
   * Opening a form-only flow meant cancelling never created a Leads row.
   */
  const createLeadFromPrelead = async (row, qualifyNotes = null) => {
    if (!row) return null;
    if (row.lead_id) return row.lead_id;

    const projectId = resolvePreleadQualifyProjectId(row, {
      isInboundAdsList,
      fallbackProjectId: resolvedProjectId ?? preleadsData.project_id
    });
    if (!projectId) {
      throw new Error(
        isInboundAdsList
          ? 'No project found for this prelead. Map this website/form source to a project first.'
          : 'No interested campaign project found for this prelead.'
      );
    }

    const mapToIdArray = (items) =>
      Array.isArray(items) ? items.map((item) => item?.id ?? item).filter(Boolean) : [];

    const payload = {
      pre_lead_id: row.pre_lead_id ?? row.id,
      name: row.name || `Prelead ${row.id}`,
      countryCode: row.countryCode,
      phone_number: String(row.phone || '').replace(/\D/g, ''),
      source:
        qualifyLeadSource ||
        row.source ||
        (isInboundAdsList ? 'website' : 'whatsapp_outbound'),
      interested_projects: [Number(projectId)],
      ...(qualifyNotes ? { qualify_notes: qualifyNotes } : {})
    };

    if (row.email && row.email !== '—') payload.email = row.email;
    if (row.alternate_phone_number && row.alternate_phone_number !== '—') {
      payload.alternate_phone_number = String(row.alternate_phone_number).replace(/\D/g, '');
    }
    if (row.occupation) payload.occupation = row.occupation;
    if (row.reference_name) payload.reference_name = row.reference_name;
    if (row.address) payload.address = row.address;
    if (row.gender) payload.gender = row.gender;
    if (row.expected_closure_date) payload.expected_clouser_date = row.expected_closure_date;

    const interestedAccommodationType = mapToIdArray(row.accommodations);
    if (interestedAccommodationType.length) {
      payload.interestedAccomodationType = interestedAccommodationType;
    }
    const projectAccommodationIds = mapToIdArray(row.project_accommodations);
    if (projectAccommodationIds.length) {
      payload.accomodations = projectAccommodationIds;
    }
    const plotAreaIds = mapToIdArray(row.plotArea);
    if (plotAreaIds.length) {
      payload.plotArea = plotAreaIds;
    }
    if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
      payload.expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
    } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
      payload.expectedBudgetRange = String(row.expected_budget);
    }

    const result = await createPreleadLeadMutation.mutateAsync(payload);
    return result?.id ?? result?.data?.id ?? null;
  };

  const handleUpdateStatus = async (row, { categoryKey, statusLabel, notes }) => {
    if (!row?.id) return;

    const noteText = formatPreleadStatusNote(categoryKey, statusLabel, notes);
    setIsUpdatingStatus(true);
    try {
      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_QUALIFIED) {
        await addPreLeadNote(row.id, noteText);
        await createLeadFromPrelead(row, noteText);
        toast.success('Lead created successfully. Prelead moved to Leads.');
        invalidateAfterLeadCreate();
        return;
      }

      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_UNQUALIFIED) {
        await unqualifyPreleadMutation.mutateAsync({ preLeadId: row.id, reason: statusLabel });
        await addPreLeadNote(row.id, noteText);
        toast.success('Marked as Unqualified.');
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
        return;
      }

      await addPreLeadNote(row.id, noteText);
      toast.success('Status updated.');
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not update status.');
      throw e;
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handlePreleadLogAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
  };

  const isMarketing = location.pathname.startsWith('/builder/marketing');
  const userVertical = Cookies.get('userVertical');
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  const isSalesExecutive = userVertical === 'Sales' && userDesignationLevel === 'Low';
  const canBulkAssign = !isMarketing && !isSalesExecutive;
  const canDeletePreleads =
    userVertical === 'Management' ||
    (userVertical === 'Sales' &&
      (userDesignationLevel === 'Medium' || userDesignationLevel === 'High'));

  const goBack = () => {
    if (isMarketing) {
      navigate('/builder/marketing/prelead-lists');
    } else {
      try {
        localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
      } catch {
        /* ignore */
      }
      navigate('/builder/sales/', { state: { secondaryTab: 'Prelead List' } });
    }
  };

  const runDownload = async () => {
    if (!numericListId) return;
    setIsDownloading(true);
    try {
      const buffer = await downloadPreLeadTemplate(numericListId);
      if (!buffer || (buffer.byteLength !== undefined && buffer.byteLength === 0)) {
        toast.error('Downloaded file is empty.');
        return;
      }
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const slug = (title || `list_${numericListId}`).replace(/[^a-zA-Z0-9-_]/g, '_');
      saveAs(blob, `Prelead_${slug}_template.xlsx`);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const runUpload = async (file) => {
    if (!file || !numericListId) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadPreLeadTemplate(numericListId, formData);
      const { errors, results, duplicates } = normalizeTemplateUploadResponse(response);

      if (errors.length > 0) {
        setUploadErrors(errors);
        setUploadSuccesses(results);
        setUploadDuplicates(duplicates);
        setIsUploadResultModalOpen(true);
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      } else {
        toast.success('Template uploaded successfully');
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const closeUploadResultModal = () => {
    setIsUploadResultModalOpen(false);
    setUploadErrors([]);
    setUploadSuccesses([]);
    setUploadDuplicates([]);
  };

  const handleRejectPreleadDuplicates = (duplicatesToReject) => {
    setUploadDuplicates((prev) =>
      prev.filter((item) => !duplicatesToReject.some((rejected) => rejected.row === item.row))
    );
    if (
      uploadDuplicates.length === duplicatesToReject.length &&
      uploadErrors.length === 0
    ) {
      closeUploadResultModal();
    }
  };

  const handleRetryPreleadUpload = () => {
    closeUploadResultModal();
    requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) runUpload(file);
  };
  return (
    <Layout>
      <UploadResultsModal
        isOpen={isUploadResultModalOpen}
        onClose={closeUploadResultModal}
        uploadSuccesses={uploadSuccesses}
        uploadErrors={uploadErrors}
        uploadDuplicates={uploadDuplicates}
        onAcceptDuplicates={() => {}}
        onRejectDuplicates={handleRejectPreleadDuplicates}
        isProcessingDuplicates={false}
        onRetryUpload={handleRetryPreleadUpload}
        fileEntityLabel="preleads"
        duplicateActionsEnabled={false}
      />

      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header backArrow onBack={goBack} data={{ name: `${isMarketing ? 'Marketing' : 'Sales'} > Prelead lists > ${title}` }} />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] min-h-[calc(100vh-5rem)] bg-gray-50 px-2 pb-10 pt-4 sm:px-4 lg:px-6">
          <div className="mx-auto max-w-screen-2xl space-y-4">
            {/* Header + stats combined */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {/* Top bar: title + buttons */}
              <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:px-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-0.5">
                  <p className="text-base font-semibold text-customBlue">{title}</p>
                  {companyLabel != null && (
                    <p className="text-sm text-gray-500">
                      Company{' '}
                      <span className="font-medium text-gray-800">{companyLabel}</span>
                    </p>
                  )}
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                  {allowWhatsAppCampaignsSidebar && (
                    <button
                      type="button"
                      onClick={() => {
                        if (waBulkMode) {
                          exitBulkModes();
                        } else {
                          enterWaBulkMode();
                        }
                      }}
                      className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors sm:w-auto ${
                        waBulkMode
                          ? 'border-customBlue bg-customBlue text-white'
                          : 'border-customBlue bg-white text-customBlue hover:bg-[#E9F5F6]'
                      }`}
                      title="Select preleads to send a WhatsApp flow">
                      <MessageCircle className="w-4 h-4 shrink-0" />
                      {waBulkMode ? 'Cancel selection' : 'Send WhatsApp flow'}
                    </button>
                  )}
                  {isFacebookList && !isMarketing && (
                    <button
                      type="button"
                      onClick={() => {
                        queryClient.invalidateQueries({
                          queryKey: ['preLeadsByListId', numericListId]
                        });
                        setIsAutoFlowModalOpen(true);
                      }}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors sm:w-auto">
                      <Workflow className="w-4 h-4 shrink-0" />
                      Auto WhatsApp flow
                      {apiList?.whatsapp_flow_id != null ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                          Linked
                        </span>
                      ) : null}
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                    className="hidden"
                    onChange={onFileChange}
                  />
                  <button
                    type="button"
                    disabled={isDownloading || isUploading}
                    onClick={runDownload}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-customBlue bg-white px-3 py-2 text-sm font-semibold text-customBlue hover:bg-[#E9F5F6] disabled:opacity-50 transition-colors sm:w-auto">
                    <Download className="w-4 h-4 shrink-0" />
                    {isDownloading ? 'Downloading…' : 'Download template'}
                  </button>
                  <button
                    type="button"
                    disabled={isDownloading || isUploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-customBlue bg-customBlue px-3 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50 transition-opacity sm:w-auto">
                    <Upload className="w-4 h-4 shrink-0" />
                    {isUploading ? 'Uploading…' : 'Upload template'}
                  </button>
                </div>
              </div>

              {/* Summary stats row */}
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                {[
                  {
                    label: 'Total Leads',
                    value: isLoading ? '—' : totalPreLeadsCount,
                    icon: Users,
                    iconBg: 'bg-customBlue/10',
                    iconColor: 'text-customBlue',
                    text: 'text-customBlue',
                    filterId: 'all'
                  },
                  {
                    label: 'Potential Qualified',
                    value: isLoading ? '—' : interestedCount,
                    icon: TrendingUp,
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-600',
                    text: 'text-green-700',
                    filterId: 'interested'
                  },
                  {
                    label: 'Not Interested',
                    value: isLoading ? '—' : unqualifiedCount,
                    icon: TrendingDown,
                    iconBg: 'bg-red-100',
                    iconColor: 'text-red-500',
                    text: 'text-red-600',
                    filterId: 'not-interested'
                  },
                  {
                    label: 'Pending',
                    value: isLoading ? '—' : pendingCount,
                    icon: Clock,
                    iconBg: 'bg-amber-100',
                    iconColor: 'text-amber-500',
                    text: 'text-amber-600',
                    filterId: 'awaiting'
                  },
                  {
                    label: 'Qualified',
                    value: isLoading ? '—' : linkedToLeadCount,
                    icon: UserCheck,
                    iconBg: 'bg-indigo-100',
                    iconColor: 'text-indigo-600',
                    text: 'text-indigo-700',
                    filterId: 'qualified'
                  },
                  {
                    // Same set as the "Pending action" filter tab.
                    label: 'Pending Telecalling',
                    value: isLoading ? '—' : pendingTelecallingCount,
                    icon: PhoneCall,
                    iconBg: 'bg-purple-100',
                    iconColor: 'text-purple-600',
                    text: 'text-purple-700',
                    filterId: 'pending-action'
                  }
                ].map(({ label, value, icon: Icon, iconBg, iconColor, text, filterId }) => {
                  const isActive = selectedFilter === filterId;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setFilterInQuery(filterId)}
                      className={`flex min-w-0 items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                        isActive
                          ? 'border-customBlue/40 bg-[#E9F5F6]'
                          : 'border-gray-100 bg-gray-50/40 hover:bg-gray-50'
                      }`}>
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                        <Icon className={`h-4 w-4 ${iconColor}`} />
                      </div>
                      <div>
                        <p className="text-[11px] font-medium leading-none text-gray-500">{label}</p>
                        <p className={`mt-1 text-2xl font-bold leading-none ${text}`}>{value}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {isFacebookList && !isMarketing && (
              <PreleadListAutoWhatsAppFlow
                isOpen={isAutoFlowModalOpen}
                onClose={() => setIsAutoFlowModalOpen(false)}
                listId={numericListId}
                list={apiList}
                projectId={resolvedProjectId}
                onLinked={() => {
                  queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
                  queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
                }}
              />
            )}

            {isLoading && <p className="text-sm text-gray-600 py-8 text-center">Loading preleads…</p>}

            {isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error?.response?.data?.message || error?.message || 'Could not load preleads.'}
              </div>
            )}

            {!isLoading && !isError && (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/50 px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    {PRELEAD_LIST_FILTER_TABS.map(({ id, label }) => {
                      const isActive = selectedFilter === id;
                      const tabCount =
                        id === 'pending-action' && !isLoading ? pendingTelecallingCount : null;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setFilterInQuery(id)}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            isActive
                              ? 'border-customBlue bg-customBlue text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}>
                          {label}
                          {tabCount != null ? ` (${tabCount})` : ''}
                        </button>
                      );
                    })}
                  </div>
                  {!isLoading && Number.isFinite(Number(paginationMeta?.total)) && (
                    <p className="text-xs font-medium text-gray-500">
                      {debouncedSearch
                        ? `${paginationMeta.total} matching “${debouncedSearch}”`
                        : selectedFilter === 'all'
                          ? `${paginationMeta.total} total`
                          : `${paginationMeta.total} matching “${PRELEAD_LIST_FILTER_CONFIG[selectedFilter]?.label ?? 'filter'}”`}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-2">
                  <div className="min-w-0 flex-1 [&>div]:h-9 [&>div]:w-full [&>div]:max-w-md [&>div]:border-gray-300 [&>div>input]:w-full [&>div>input]:text-sm">
                    <Search
                      query={searchInput}
                      onChange={setSearchInput}
                      placeholder="Search name, phone, email…"
                    />
                  </div>
                  {canBulkAssign && (
                    <button
                      type="button"
                      onClick={() => {
                        if (assignBulkMode) {
                          exitBulkModes();
                        } else {
                          enterAssignBulkMode();
                        }
                      }}
                      className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                        assignBulkMode
                          ? 'border-customBlue bg-customBlue text-white'
                          : 'border-customBlue bg-white text-customBlue hover:bg-[#E9F5F6]'
                      }`}
                      title="Select preleads to assign to a sales user">
                      <UserPlus className="w-4 h-4 shrink-0" />
                      {assignBulkMode ? 'Cancel selection' : 'Bulk assign'}
                    </button>
                  )}
                  {canDeletePreleads && (
                    <button
                      type="button"
                      onClick={() => {
                        if (deleteBulkMode) {
                          exitBulkModes();
                        } else {
                          enterDeleteBulkMode();
                        }
                      }}
                      className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                        deleteBulkMode
                          ? 'border-rose-600 bg-rose-600 text-white'
                          : 'border-rose-600 bg-white text-rose-600 hover:bg-rose-50'
                      }`}
                      title="Select preleads to delete in bulk">
                      <Trash2 className="w-4 h-4 shrink-0" />
                      {deleteBulkMode ? 'Cancel delete' : 'Bulk delete'}
                    </button>
                  )}
                </div>
                <PreleadTable
                  rows={tableRows}
                  showAction={!isMarketing || canDeletePreleads}
                  showFlowResponse={!isMarketing && !isFacebookList}
                  showFacebookFormResponse={!isMarketing && isFacebookList}
                  showGoogleAdsFormResponse={!isMarketing && isGoogleAdsList}
                  showWebsiteFormResponse={!isMarketing && isWebsiteList}
                  showWhatsAppCampaign={!isFacebookList}
                  showWhatsAppInbox={allowWhatsAppCampaignsSidebar}
                  listProjectId={resolvedProjectId}
                  selectionMode={selectionMode}
                  selectedIds={bulkSelectedIds}
                  selectableIds={bulkSelectableIds}
                  selectAllChecked={bulkSelectAllChecked}
                  onToggleSelect={toggleBulkPrelead}
                  onSelectAll={handleBulkSelectAll}
                  selectionTitle={
                    deleteBulkMode
                      ? 'Select prelead for bulk delete'
                      : assignBulkMode
                        ? 'Select prelead for bulk assign'
                        : 'Select prelead for WhatsApp flow'
                  }
                  onUpdateStatus={handleUpdateStatus}
                  onLogAdded={handlePreleadLogAdded}
                  onDeleteRow={
                    canDeletePreleads && !selectionMode
                      ? (row) => setPendingDeleteIds([row.id])
                      : undefined
                  }
                  emptyMessage={
                    debouncedSearch
                      ? `No preleads found for “${debouncedSearch}”.`
                      : selectedFilter === 'all'
                        ? 'No preleads in this list yet. Download the template, add rows, and upload the file.'
                        : `No preleads found for “${PRELEAD_LIST_FILTER_CONFIG[selectedFilter]?.label ?? 'filter'}”.`
                  }
                  isStatusSubmitting={
                    isUpdatingStatus ||
                    createPreleadLeadMutation.isPending ||
                    unqualifyPreleadMutation.isPending
                  }
                />
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !isError && Number(paginationMeta?.total) > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <PaginationComponent
                  page={page}
                  totalPages={paginationMeta.totalPages}
                  onPageChange={(nextPage) => {
                    const boundedPage = Math.max(1, Math.min(paginationMeta.totalPages, Number(nextPage)));
                    setPage(boundedPage);
                  }}
                  isFetching={isFetching}
                  pageSize={pageLimit}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPageSizeChange={setPageLimit}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectionMode && (
        <div className="fixed bottom-6 right-6 z-[60] lg:right-10">
          <div className="flex items-center gap-4 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
            <span className="text-sm font-medium text-gray-700">
              {bulkSelectedIds.length} / {maxSelected} selected
            </span>
            {waBulkMode && (
              <button
                type="button"
                onClick={handleOpenWaFlowPicker}
                disabled={bulkSelectedIds.length === 0}
                className="flex items-center gap-2 rounded-full bg-customBlue px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50">
                <MessageCircle size={16} />
                Send WhatsApp flow
              </button>
            )}
            {assignBulkMode && (
              <button
                type="button"
                onClick={handleOpenAssignModal}
                disabled={bulkSelectedIds.length === 0 || bulkAssignMutation.isPending}
                className="flex items-center gap-2 rounded-full bg-customBlue px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50">
                <UserPlus size={16} />
                Assign sales
              </button>
            )}
            {deleteBulkMode && (
              <button
                type="button"
                onClick={handleConfirmBulkDelete}
                disabled={bulkSelectedIds.length === 0 || deletePreleadsMutation.isPending}
                className="flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
                <Trash2 size={16} />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={exitBulkModes}
              className="text-sm font-medium text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {pendingDeleteIds?.length > 0 && (
        <Modal
          width={40}
          confirmOnClose={false}
          isDirty={false}
          name={
            pendingDeleteIds.length === 1
              ? 'Delete prelead'
              : `Delete ${pendingDeleteIds.length} preleads`
          }
          onClose={() => {
            if (deletePreleadsMutation.isPending) return;
            setPendingDeleteIds(null);
          }}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {pendingDeleteIds.length === 1
                ? 'Are you sure you want to delete this prelead? This cannot be undone.'
                : `Are you sure you want to delete ${pendingDeleteIds.length} preleads? This cannot be undone.`}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                disabled={deletePreleadsMutation.isPending}
                onClick={() => setPendingDeleteIds(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                displayType="primary-red"
                disabled={deletePreleadsMutation.isPending}
                onClick={() => deletePreleadsMutation.mutate(pendingDeleteIds)}>
                {deletePreleadsMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {assignModalOpen && (
        <BulkAssignPreleadsModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          selectedCount={bulkSelectedIds.length}
          projectId={resolvedProjectId}
          isSubmitting={bulkAssignMutation.isPending}
          onConfirm={handleBulkAssignConfirm}
        />
      )}

      {waFlowPickerOpen && (
        <BulkSendFlowModal
          projectId={resolvedProjectId}
          recipientType="pre_lead_list"
          preLeadListId={numericListId}
          preLeadIds={bulkSelectedIds}
          onClose={() => setWaFlowPickerOpen(false)}
          onSent={() => {
            exitBulkModes();
            queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
          }}
        />
      )}

    </Layout>
  );
};

export default PreleadListDetailPage;
